import React, { useState, useMemo } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Chip,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  Folder,
  FolderOpen,
  Description,
  KeyboardArrowRight,
  KeyboardArrowDown,
  Download,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import moment from "moment";

// Build tree structure from files with folder paths
function buildTree(files, currentPath = "") {
  const tree = {};
  
  if (!files || !Array.isArray(files)) {
    return [];
  }
  
  files.forEach((file) => {
    if (!file || !file.id) return; // Skip invalid files
    
    let path = file.folderPath || "";
    path = path.replace(/\\/g, '/'); // Normalize separators

    // Attempt to strip currentPath prefix to prevent duplication of breadcrumb hierarchy
    if (currentPath && path.startsWith(currentPath)) {
        path = path.substring(currentPath.length);
    } else if (currentPath) {
        // Try matching without the first segment (e.g. hidden root folder)
        const parts = currentPath.split('/');
        parts.shift(); 
        const subPath = parts.join('/');
        if (subPath && path.startsWith(subPath)) {
            path = path.substring(subPath.length);
        }
    }
    // Remove leading slashes
    path = path.replace(/^[\/\\]+/, "");

    const pathParts = path ? path.split("/").filter(Boolean) : [];
    
    // Start from root
    let current = tree;
    
    // Create folder nodes
    pathParts.forEach((part, index) => {
      // Fix: Avoid adding 'files' or '_files' as a folder node
      if (part === "files" || part === "_files") return;

      if (!current[part]) {
        current[part] = {
          type: "folder",
          name: part,
          path: pathParts.slice(0, index + 1).join("/"),
          children: {},
          files: [],
        };
      }
      current = current[part].children;
    });
    
    // Add file to the appropriate folder
    if (pathParts.length === 0) {
      // Root level file
      if (!tree._files) {
        tree._files = [];
      }
      tree._files.push({ type: "file", ...file });
    } else {
      // File in a folder
      const folderPath = pathParts.join("/");
      let folderNode = tree;
      for (const part of pathParts) {
        if (!folderNode[part]) break;
        folderNode = folderNode[part];
      }
      if (folderNode && folderNode !== tree) {
        if (!folderNode.files) {
          folderNode.files = [];
        }
        folderNode.files.push({ type: "file", ...file });
      }
    }
  });
  
  // Convert tree object to array format
  function convertToArray(node, parentPath = "") {
    const result = [];
    
    // Add folders
    Object.keys(node)
      .filter((key) => key !== "_files" && typeof node[key] === "object")
      .sort()
      .forEach((key) => {
        const folder = node[key];
        const fullPath = parentPath ? `${parentPath}/${folder.name}` : folder.name;
        result.push({
          type: "folder",
          name: folder.name,
          path: fullPath,
          children: convertToArray(folder.children, fullPath),
          files: folder.files || [],
        });
      });
    
    // Add root level files
    if (node._files && node._files.length > 0) {
      node._files.forEach((file) => {
        result.push({
          type: "file",
          ...file,
        });
      });
    }
    
    return result;
  }
  
  return convertToArray(tree);
}

function FileTree({
  files,
  currentPath = "",
  onDownload,
  onApprove,
  onReject,
  showActions = false,
  downloadingFileID = -1,
  reviewingFileAction = null,
  isAdmin = false,
}) {
  const [expandedFolders, setExpandedFolders] = useState({});

  const treeData = useMemo(() => {
    if (!files || !Array.isArray(files)) {
      return [];
    }
    return buildTree(files.filter(f => f && f.id), currentPath); // Filter out invalid files
  }, [files, currentPath]);

  const handleToggleFolder = (path) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const renderTreeNode = (node, depth = 0) => {
    if (!node) return null;
    
    if (node.type === "file") {
      const isInReview = node.status === "in_review";
      const isDownloading = node.id === downloadingFileID;
      const isReviewing =
        reviewingFileAction === `${node.id}-approved` ||
        reviewingFileAction === `${node.id}-rejected`;

      return (
        <ListItemButton
          key={`file-${node.id}`}
          sx={{
            pl: 2 + depth * 2,
            py: 0.5,
            borderRadius: 1,
            mb: 0.25,
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Description fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  sx={{ padding: 0, textTransform: "none" }}
                  onClick={() => {
                    if (!isAdmin && isInReview) {
                      return; // Non-admin cannot download files under review
                    }
                    onDownload && onDownload(node.id, node.name, node.filePath);
                  }}
                  disabled={isDownloading || (!isAdmin && isInReview)}
                  variant="text"
                  color="primary"
                >
                  {node.name}
                </Button>
                {isInReview && (
                  <Chip size="small" label="In Review" color="warning" />
                )}
                {isDownloading && <CircularProgress size={16} />}
              </Box>
            }
            secondary={
              <Box>
                {node.Uploader && (
                  <Typography variant="caption" color="text.secondary">
                    Uploaded by: {node.Uploader.name} ({node.Uploader.email})
                  </Typography>
                )}
                {node.uploaded_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {moment(node.uploaded_at).format("YYYY-MM-DD HH:mm")}
                  </Typography>
                )}
                {!isAdmin && isInReview && (
                  <Typography variant="caption" color="error" sx={{ display: "block" }}>
                    This file is under review by the admin.
                  </Typography>
                )}
              </Box>
            }
          />
          {showActions && isAdmin && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => onDownload && onDownload(node.id, node.name, node.filePath)}
                disabled={isDownloading}
              >
                {isDownloading ? <CircularProgress size={20} /> : <Download />}
              </IconButton>
              <IconButton
                size="small"
                color="success"
                onClick={() => onApprove && onApprove(node.id)}
                disabled={isReviewing}
              >
                <CheckCircle />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => onReject && onReject(node.id)}
                disabled={isReviewing}
              >
                <Cancel />
              </IconButton>
            </Box>
          )}
          {!isAdmin && isInReview && (
            <Typography variant="caption" color="error" sx={{ ml: 2 }}>
              Cannot download - under review
            </Typography>
          )}
        </ListItemButton>
      );
    }

    // Folder node
    const isExpanded = expandedFolders[node.path] || depth === 0; // Auto-expand root level
    const hasChildren = (node.children && node.children.length > 0) || (node.files && node.files.length > 0);

    return (
      <React.Fragment key={`folder-${node.path}`}>
        <ListItemButton
          onClick={() => handleToggleFolder(node.path)}
          sx={{
            pl: 2 + depth * 2,
            py: 0.5,
            borderRadius: 1,
            mb: 0.25,
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleFolder(node.path);
                }}
                sx={{ mr: 0.5 }}
              >
                {isExpanded ? (
                  <KeyboardArrowDown fontSize="small" />
                ) : (
                  <KeyboardArrowRight fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box component="span" sx={{ width: 32, mr: 0.5 }} />
            )}
            {isExpanded ? (
              <FolderOpen color="primary" fontSize="small" />
            ) : (
              <Folder fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText primary={node.name} />
        </ListItemButton>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {node.children && Array.isArray(node.children) && node.children.map((child) => {
              if (!child) return null;
              return renderTreeNode(child, depth + 1);
            })}
            {node.files && Array.isArray(node.files) && node.files.map((file) => {
              if (!file) return null;
              return renderTreeNode({ type: "file", ...file }, depth + 1);
            })}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  if (!treeData || treeData.length === 0) {
    return (
      <Typography sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
        No files to display.
      </Typography>
    );
  }

  return (
    <List>
      {treeData && Array.isArray(treeData) && treeData.map((node) => {
        if (!node) return null;
        return renderTreeNode(node);
      })}
    </List>
  );
}

export default FileTree;

